"use client"

import Link from "next/link"
import { Separator } from "@/components/ui/separator"
import Image from "next/image"
import wordmark from "@/app/design_system/wordmark_black.svg"
import { motion, useInView } from "framer-motion"
import { useRef } from "react"
import { 
  buttonVariants,
  fromLeftVariants, 
  fromRightVariants,
  createScrollConfig
} from "@/utils/animations"

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null)
  const isInView = useInView(footerRef, createScrollConfig(-150, 0.05))

  return (
    <footer
      ref={footerRef}
      className="bg-background py-16 lg:py-24"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="container px-6 mx-auto flex flex-col gap-12 lg:gap-16">
        {/* Top Section */}
        <motion.div 
          className="flex flex-col items-center gap-12 w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.1 }}
          >
            <Link href="/" aria-label="Go to homepage">
              <Image src={wordmark} alt="Paragraph" width={40} height={40} />
            </Link>
          </motion.div>

          {/* Main Navigation */}
          <nav
            className="flex flex-col md:flex-row items-center gap-8"
            aria-label="Footer navigation"
          >
            <motion.div variants={buttonVariants} initial="hidden" animate={isInView ? "visible" : "hidden"} whileHover="hover">
              <Link
                href="/"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Home
              </Link>
            </motion.div>
            <motion.div variants={buttonVariants} initial="hidden" animate={isInView ? "visible" : "hidden"} whileHover="hover" transition={{ delay: 0.1 }}>
              <Link
                href="/home"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Explore
              </Link>
            </motion.div>
            <motion.div variants={buttonVariants} initial="hidden" animate={isInView ? "visible" : "hidden"} whileHover="hover" transition={{ delay: 0.2 }}>
              <Link
                href="mailto:hello@paragraph.com"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Contact
              </Link>
            </motion.div>
          </nav>
        </motion.div>

        {/* Section Divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={isInView ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Separator role="presentation" />
        </motion.div>

        {/* Bottom Section */}
        <div className="w-full flex flex-col-reverse lg:flex-row lg:justify-between items-center gap-12 lg:gap-6">
          {/* Copyright Text */}
          <motion.p 
            className="text-muted-foreground text-center lg:text-left"
            variants={fromLeftVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <span>Copyright © {new Date().getFullYear()}</span>{" "}
            <Link href="/" className="hover:underline">
              Paragraph Technologies Inc
            </Link>
            . All rights reserved.
          </motion.p>

          {/* Legal Navigation */}
          <motion.nav
            className="flex flex-col md:flex-row items-center gap-6 md:gap-8"
            aria-label="Legal links"
            variants={fromRightVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <Link
              href="/privacy"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-of-use"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Terms of Use
            </Link>
            <Link
              href="/content-guidelines"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Content Guidelines
            </Link>
          </motion.nav>
        </div>
      </div>
    </footer>
  )
}
